import { DataTable } from "mantine-datatable";
import React, { useEffect, useState } from "react";
import CustomPagination from "../../components/Layouts/CustomPagination";
import { FiBell, FiRefreshCw, FiSend } from "react-icons/fi";
import Swal from "sweetalert2";
import DropdownCustom from "../../components/DropdownCustom";
import CountriesJSON from "../../components/countries.json";
import moment from "moment";
import UserDetailsService from "../../service/userdetails.service";
import DashboardService from "../../service/dashboard.service";
import NotifyService from "../../service/notify.service";

function UserDetails() {
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState([]);
  // const [mobileNo, setMobileNo] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState("");
  const [page, setPage] = useState(1);
  const PAGE_SIZES = [10, 20, 30, 50, 100];
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  //   const [status, setStatus] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [totalData, setTotalData] = useState(0);
  const [date, setDate] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [isOnline, setIsOnline] = useState("");
  const [selectedMCC, setSelectedMCC] = useState("");
  const [selectedMNC, setSelectedMNC] = useState("");
  const [countryOptions, setCountryOptions] = useState([]);
  const [mncData, setMncData] = useState([]);
  const [mccData, setMccData] = useState([]);
  const [isFirst, setIsFirst] = useState(false);
  const [mobileNumber, setMobileNumber] = useState("");
  const [debouncedMobile, setDebouncedMobile] = useState("");
  const [verified, setVerified] = useState(""); // "", "true", "false"
  const [fromSim, setFromSim] = useState(""); // number source: "", "true" (SIM), "false" (typed)
  const [bulkBusy, setBulkBusy] = useState(false);
  const [crossBusy, setCrossBusy] = useState(false);
  const [confirmState, setConfirmState] = useState(null); // custom confirm modal state

  const getMncMc = async () => {
    try {
      const mccDataResponse = await UserDetailsService.getMncMcc(true);

      const mccDataResponseAll = mccDataResponse.data.apiresponse.data;

      const options = [];

      options.push({ value: "", label: "All" });

      mccDataResponseAll.forEach((item) => {
        if (item !== null) {
          options.push({
            value: item,
            label: item,
          });
        }
      });
      setMncData(options);

      const mncDataResponse = await UserDetailsService.getMncMcc(false);

      const mncDataResponseAll = mncDataResponse.data.apiresponse.data;
      // const optionsMNC = mncDataResponseAll.map((item) => ({
      //   value: item === null ? null : item,
      //   label: item === null ? "All" : item,
      // }));

      const optionsMNC = [];

      optionsMNC.push({ value: "", label: "All" });

      mncDataResponseAll.forEach((item) => {
        if (item !== null) {
          optionsMNC.push({
            value: item,
            label: item,
          });
        }
      });
      setMccData(optionsMNC);
    } catch (error) {
      showMessage(error.message, "error");
    }
  };

  useEffect(() => {
    getMncMc();
  }, []);

  const showMessage = (msg, type) => toastMsg(type, msg || "Something went wrong");

  const handleChange = (type, page) => {
    const total = totalData / pageSize;
    const newPage = Math.ceil(total);
    if (type === "Next") {
      if (page !== newPage) {
        setPage(page + 1);
      }
    } else if (type === "Previous") {
      if (page !== 1) {
        setPage(page - 1);
      }
    } else if (type === "First") {
      if (page !== 1) {
        setPage(1);
      }
    } else if (type === "Last") {
      if (page !== newPage) {
        setPage(newPage);
      }
    } else {
      setPage(page);
    }
  };

  const getAllUser = async () => {
    setIsFirst(true);
    if (isFirst) {
      setIsLoading(true);
    } else {
      setIsLoading(false);
    }

    // Installation-date range, whole days in the viewer's timezone (the server used its own
    // UTC day before, so a picked day was off by the timezone offset).
    const valid = (d) => d && d !== "Invalid date";
    const formattedDate = {
      from: valid(date) ? moment(date, "YYYY-MM-DD").startOf("day").valueOf() : "",
      to: valid(dateTo) ? moment(dateTo, "YYYY-MM-DD").endOf("day").valueOf() : "",
    };

    try {
      const response = await UserDetailsService.getAllUser(
        // mobileNo,
        selectedCountry,
        formattedDate,
        isOnline,
        page,
        pageSize,
        String(selectedMNC),
        String(selectedMCC),
        debouncedMobile,
        verified,
        fromSim
      );

      setData(response.data.apiresponse.data.user);
      setTotalData(response.data.apiresponse.data.total);
    } catch (error) {
      showMessage(error.message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  // Any filter change starts again from page 1 (staying on page 2 of a shorter result showed
  // "Showing 11 to 8 of 8" and an empty table).
  useEffect(() => {
    setPage(1);
  }, [selectedCountry, selectedMCC, selectedMNC, isOnline, verified, fromSim, date, dateTo, debouncedMobile]);

  // Safety net: if the list shrinks under the current page (e.g. users removed), go to the last page.
  useEffect(() => {
    const last = Math.max(1, Math.ceil(totalData / pageSize));
    if (page > last) setPage(last);
  }, [totalData, pageSize, page]);

  // Debounce the mobile-number filter so typing doesn't refetch / restart the 5s poll each keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedMobile(mobileNumber), 400);
    return () => clearTimeout(t);
  }, [mobileNumber]);

  useEffect(() => {
    getAllUser();
    const intervalId = setInterval(() => {
      setIsFirst(false);
      getAllUser();
    }, 5000);

    return () => clearInterval(intervalId);
  }, [selectedCountry, selectedMCC, selectedMNC, isOnline, verified, fromSim, date, dateTo, debouncedMobile, page, pageSize]);

  // Bulk-wake every device matching the CURRENT filters (reuses the same filter state as the
  // list). Maps them to the /admin/notify-filter API, then refreshes so you SEE who came online.
  const bulkNotify = async () => {
    const body = {};
    if (selectedCountry) body.country = selectedCountry;
    if (selectedMCC) body.mcc = String(selectedMCC);
    if (selectedMNC) body.mnc = String(selectedMNC);
    if (isOnline === "true" || isOnline === "false") body.online = isOnline === "true";
    if (verified === "true" || verified === "false") body.verified = verified === "true";
    if (mobileNumber) body.mobileNumber = Number(mobileNumber);

    // Plain-words summary of the active filters (not raw JSON).
    const parts = [];
    if (body.country) parts.push(body.country);
    if (body.online !== undefined) parts.push(body.online ? "Online" : "Offline");
    if (body.verified !== undefined) parts.push(body.verified ? "Verified" : "Unverified");
    if (body.mcc) parts.push(`MCC ${body.mcc}`);
    if (body.mnc) parts.push(`MNC ${body.mnc}`);
    if (body.mobileNumber) parts.push(`Number ${body.mobileNumber}…`);
    const confirm = await confirmDialog({
      title: "Bulk notify?",
      text:
        parts.length === 0
          ? "No filters set — this wakes EVERY device (up to 500)."
          : `Wake all devices matching: ${parts.join(" · ")}`,
      note: "Free — push notification only",
      confirmButtonText: "Notify",
    });
    if (!confirm.isConfirmed) return;

    setBulkBusy(true);
    try {
      const res = await NotifyService.notifyByFilter(body);
      const d = res?.data?.apiresponse?.data || {};
      toastMsg("success", `Woke ${d.notified ?? 0} device(s)`);
      setIsFirst(false);
      getAllUser(); // refresh — online devices will flip as they answer
    } catch (e) {
      showMessage("Bulk notify failed", "error");
    } finally {
      setBulkBusy(false);
    }
  };

  // One clean toast everywhere: top-right, auto-dismiss with a progress bar, icon by type
  // ("danger" -> the error icon). No solid colour-block popups.
  const toastMsg = (type, title) =>
    Swal.mixin({
      toast: true,
      position: "top-end",
      showConfirmButton: false,
      showCloseButton: true,
      timer: 2800,
      timerProgressBar: true,
      customClass: { popup: "rounded-lg shadow-lg !p-3 text-sm" },
    }).fire({ icon: type === "danger" ? "error" : type, title });

  // Custom confirm dialog (NOT SweetAlert) — a clean card rendered at the bottom of this
  // component. Promise-based:  const ok = await confirmDialog({...}); if (!ok.isConfirmed) return;
  const confirmDialog = (opts = {}) =>
    new Promise((resolve) => setConfirmState({ ...opts, resolve }));

  const closeConfirm = (isConfirmed) => {
    if (confirmState) confirmState.resolve({ isConfirmed });
    setConfirmState(null);
  };

  // The list's current filters, in the order filterParams() takes them.
  const currentFilters = () => {
    const valid = (d) => d && d !== "Invalid date";
    return [
      selectedCountry,
      {
        from: valid(date) ? moment(date, "YYYY-MM-DD").startOf("day").valueOf() : "",
        to: valid(dateTo) ? moment(dateTo, "YYYY-MM-DD").endOf("day").valueOf() : "",
      },
      isOnline,
      String(selectedMNC),
      String(selectedMCC),
      mobileNumber,
      verified,
      fromSim,
    ];
  };

  // Cross one unverified user: one gateway SMS; the row turns Verified when the phone reports it.
  const crossOne = async (row) => {
    const ok = await confirmDialog({
      title: "Cross-check?",
      text: `Send a verification SMS to ${row.mobileNumber}?`,
      note: "1 gateway SMS (paid)",
      confirmButtonText: "Send",
      danger: true,
    });
    if (!ok.isConfirmed) return;
    try {
      await UserDetailsService.crossUser(row.id);
      toastMsg("success", `Cross sent to ${row.mobileNumber}`);
    } catch (e) {
      toastMsg("danger", e?.response?.data?.apierror?.message || e?.response?.data?.message || "Cross failed");
    }
  };

  // Remote self-loop (RSL): a FREE admin action — pushes a self-loop command the device obeys
  // regardless of its country's verify mode (and whether it's already verified). Verifies when the
  // looped-back SMS is reported.
  const selfLoopOne = async (row) => {
    const ok = await confirmDialog({
      title: "Remote self-loop?",
      text: `Ask ${row.mobileNumber}'s phone to verify itself with its own SIM?`,
      note: "Free — no gateway SMS",
      confirmButtonText: "Send",
    });
    if (!ok.isConfirmed) return;
    try {
      await UserDetailsService.selfLoopUser(row.id);
      toastMsg("success", `Self-loop sent to ${row.mobileNumber}`);
      getAllUser(); // icon turns green right away
    } catch (e) {
      toastMsg("danger", e?.response?.data?.apierror?.message || e?.response?.data?.message || "Self-loop failed");
    }
  };

  // Bulk cross: every UNVERIFIED user matching the current filters (max 50, skips recent).
  const bulkCross = async () => {
    setCrossBusy(true);
    try {
      const pre = (await UserDetailsService.crossByFilter(currentFilters(), true))?.data?.apiresponse?.data || {};
      if (!pre.toSend) {
        toastMsg("info", `No unverified users to cross (skipped ${pre.skipped ?? 0})`);
        return;
      }
      const ok = await confirmDialog({
        title: "Bulk cross-check?",
        text: `Send a verification SMS to ${pre.toSend} unverified user(s)?`,
        note:
          `${pre.toSend} gateway SMS (paid)` +
          (pre.skipped ? ` · ${pre.skipped} skipped (sent < 10 min ago)` : "") +
          (pre.capped ? " · max 50 per click" : ""),
        confirmButtonText: "Send",
        danger: true,
      });
      if (!ok.isConfirmed) return;
      const res = (await UserDetailsService.crossByFilter(currentFilters(), false))?.data?.apiresponse?.data || {};
      toastMsg("success", `Sending ${res.toSend ?? 0} · skipped ${res.skipped ?? 0}`);
    } catch (e) {
      toastMsg("danger", "Bulk cross failed");
    } finally {
      setCrossBusy(false);
    }
  };

  const handleDataPerPage = (e) => {
    setPageSize(Number(e.target.value));
    setPage(1);
  };

  const handleDateChange = (selectedDate) => {
    const date = moment(selectedDate.target.value).format("YYYY-MM-DD");
    setDate(date);
    setPage(1);
  };
  const handleDateToChange = (e) => {
    setDateTo(e.target.value ? moment(e.target.value).format("YYYY-MM-DD") : "");
    setPage(1);
  };
  //   const handleClick = (selectedDate) => {
  //     const date = moment(selectedDate.target.value).format("YYYY-MM-DD");
  //     setDate(date);
  //   };

  const handleClearFilters = () => {
    setIsOnline("");
    setVerified("");
    setFromSim("");
    setSelectedCountry("");
    setSelectedMCC("");
    setSelectedMNC("");
    setMobileNumber("");
    setDate("");
    setDateTo("");
    setSelectedStatus("");
    setPage(1);
  };

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await UserDetailsService.getCountry();
        const countries = response.data.apiresponse.data;
        const options = countries.map((item) => ({
          value: item,
          label: item,
        }));

        const newObject = [{ value: "", label: "All" }, ...options];

        setCountryOptions(newObject);
        // setCountryOptions(options);
      } catch (error) {
        console.error("Failed to fetch countries:", error);
      }
    };

    fetchCountries();
  }, []);

  const sendNotification = async (id) => {
    try {
      await DashboardService.dashboardPushNotification([id]);
      toastMsg("success", "Notification sent");
      getAllUser();
    } catch (error) {
      toastMsg("danger", error.message || "Failed to send notification");
    }
  };

  return (
    <>
      <h5 className="text-lg font-semibold dark:text-white-light mb-3">
        User Details
      </h5>

      {/* <DateRangePicker
        initialSettings={{ startDate: "09/01/2024", endDate: "10/01/2024" }}
        // renges={selectionRange}
        // onchange={handleSelect}
      >
        <button className="border-2 rounded-md p-2">Select a Date</button>
      </DateRangePicker> */}
      {/* ONE row: filters (flex) · bulk actions · Clear on the far right. Small (h-8, text-xs). */}
      <div className="allfilter flex items-center gap-1.5 w-full mb-4">
        <div className="flex-1 min-w-[100px]">
          <input
            type="number"
            placeholder="Mobile Number"
            value={mobileNumber}
            onChange={(e) => setMobileNumber(e.target.value)}
            className="w-full h-8 border border-gray-300 rounded-md px-2 text-xs"
          />
        </div>
        <input
          type="date"
          title="Installed from"
          value={date}
          max={dateTo || undefined}
          onChange={handleDateChange}
          className="w-[118px] shrink-0 h-8 border border-gray-300 rounded-md px-1.5 text-xs"
        />
        <span className="text-xs text-gray-500 shrink-0">to</span>
        <input
          type="date"
          title="Installed to"
          value={dateTo}
          min={date || undefined}
          onChange={handleDateToChange}
          className="w-[118px] shrink-0 h-8 border border-gray-300 rounded-md px-1.5 text-xs"
        />

        {countryOptions.length > 0 && (
          <div className="flex-1 min-w-[90px]">
            <DropdownCustom
              data={countryOptions}
              setData={setSelectedCountry}
              name="Country"
              selectedValue={selectedCountry}
              compact
            />
          </div>
        )}

        <div className="flex-1 min-w-[90px]">
          <DropdownCustom
            data={[
              { label: "All", value: "" },
              { label: "Online", value: "true" },
              { label: "Offline", value: "false" },
            ]}
            setData={setIsOnline}
            name="Status"
            selectedValue={isOnline}
            compact
          />
        </div>

        <div className="flex-1 min-w-[90px]">
          <DropdownCustom
            data={[
              { label: "All", value: "" },
              { label: "Verified", value: "true" },
              { label: "Unverified", value: "false" },
            ]}
            setData={setVerified}
            name="Verification"
            selectedValue={verified}
            compact
          />
        </div>

        <div className="flex-1 min-w-[90px]">
          <DropdownCustom
            data={[
              { label: "All", value: "" },
              { label: "From SIM", value: "true" },
              { label: "Typed", value: "false" },
            ]}
            setData={setFromSim}
            name="Number source"
            selectedValue={fromSim}
            compact
          />
        </div>

        {mccData.length > 0 && (
          <div className="flex-1 min-w-[90px]">
            <DropdownCustom
              data={mccData}
              setData={setSelectedMCC}
              name="MCC"
              selectedValue={selectedMCC}
              compact
            />
          </div>
        )}

        {mncData.length > 0 && (
          <div className="flex-1 min-w-[90px]">
            <DropdownCustom
              data={mncData}
              setData={setSelectedMNC}
              name="MNC"
              selectedValue={selectedMNC}
              compact
            />
          </div>
        )}

        {/* Bulk actions over the CURRENT filters. */}
        <button
          type="button"
          disabled={bulkBusy}
          onClick={bulkNotify}
          className="shrink-0 h-8 bg-primary text-white rounded-md px-3 text-xs font-semibold whitespace-nowrap disabled:opacity-50"
          title="Wake all devices matching the current filters"
        >
          {bulkBusy ? "Notifying…" : "Bulk Notify"}
        </button>
        <button
          type="button"
          disabled={crossBusy}
          onClick={bulkCross}
          className="shrink-0 h-8 bg-warning text-white rounded-md px-3 text-xs font-semibold whitespace-nowrap disabled:opacity-50"
          title="Send a verification SMS to the UNVERIFIED users matching the current filters (max 50)"
        >
          {crossBusy ? "Crossing…" : "Bulk Cross"}
        </button>
        <button
          type="button"
          onClick={handleClearFilters}
          className="shrink-0 h-8 border border-gray-300 text-gray-600 rounded-md px-3 text-xs font-semibold whitespace-nowrap hover:bg-gray-100"
          title="Clear all filters"
        >
          Clear
        </button>
      </div>

      <div className="overflow-x-auto">
        <DataTable
          noRecordsText="No results match your search query"
          highlightOnHover
          className="whitespace-nowrap table-hover users-fixed-cols"
          records={data}
          columns={[
            {
              accessor: "id",
              width: "4%",
              title: "ID",
              // sortable: true,
              render: ({ id }, index) => (
                <div>
                  <span>{(page - 1) * pageSize + index + 1}</span>
                </div>
              ),
            },
            {
              accessor: "mobileNumber",
              width: "10%",
              title: "Mobile No.",
              render: ({ mobileNumber, fromSim }) => (
                <div className="flex items-center gap-1.5">
                  <span className="!text-xs">{mobileNumber}</span>
                  {fromSim && (
                    <span
                      title="Number read from the SIM at sign-up"
                      className="rounded px-1 py-px text-[10px] font-bold text-info bg-info/15"
                    >
                      SIM
                    </span>
                  )}
                </div>
              ),
            },
            {
              accessor: "countryCode",
              width: "4%",
              title: "ISO",
              render: ({ countryCode }) => (
                <div className="">
                  <span className="!text-xs">{countryCode}</span>
                </div>
              ),
            },
            {
              accessor: "country",
              width: "12%",
              title: "Country Name",
              render: ({ country, countryCode }) => {
                // `country` arrives as an ISO code or a name in any case ("INDONESIA",
                // "India"); show the standard name from the ISO column so the list is uniform.
                const up = (v) => String(v || "").toUpperCase();
                const name =
                  CountriesJSON.find((c) => up(c.value) === up(countryCode))?.label ||
                  CountriesJSON.find(
                    (c) => up(c.value) === up(country) || up(c.label) === up(country)
                  )?.label ||
                  country;
                return (
                  <div className="truncate" title={name}>
                    <span className="!text-xs">{name}</span>
                  </div>
                );
              },
            },
            {
              accessor: "mcc",
              width: "4%",
              title: "MCC",
              render: ({ mcc }) => (
                <div className="">
                  <span className="!text-xs">{mcc !== null ? mcc : "-"}</span>
                </div>
              ),
            },
            {
              accessor: "mnc",
              width: "4%",
              title: "MNC",
              render: ({ mnc }) => (
                <div className="">
                  <span className="!text-xs">{mnc !== null ? mnc : "-"}</span>
                </div>
              ),
            },
            {
              accessor: "networkName",
              width: "14%",
              title: "Mobile Network Name",
              render: ({ networkName }) => (
                <div className="truncate" title={networkName}>
                  <span className="!text-xs">{networkName}</span>
                </div>
              ),
            },
            {
              accessor: "creationDate",
              width: "10%",
              title: "Installation Date",
              // Latest install (a reinstall counts as a new install); hover shows the FIRST sign-up.
              render: ({ creationDate, installedAt }) => {
                const ok = (v) => v !== null && v !== undefined && v !== "" && v !== "null" && Number(v) > 0;
                const shown = ok(installedAt) ? installedAt : creationDate;
                const fmt = (v) => moment(Number(v)).format("DD/MM/YYYY HH:mm");
                const reinstalled = ok(installedAt) && ok(creationDate) && fmt(installedAt) !== fmt(creationDate);
                return (
                  <div title={ok(creationDate) ? `First signed up: ${fmt(creationDate)}` : undefined}>
                    <span className="!text-xs">{ok(shown) ? fmt(shown) : "--"}</span>
                    {reinstalled && <span className="ml-1 text-[10px] text-slate-400">↻</span>}
                  </div>
                );
              },
            },
            {
              accessor: "lastActiveDate",
              width: "10%",
              title: "Last Active Date",
              render: ({ lastActiveDate }) => (
                <div className="">
                  <span className="!text-xs">
                    {lastActiveDate !== "0" &&
                    lastActiveDate !== 0 &&
                    lastActiveDate !== "" &&
                    lastActiveDate !== null &&
                    lastActiveDate !== undefined &&
                    lastActiveDate !== "null"
                      ? moment(Number(lastActiveDate)).format(
                          "DD/MM/YYYY HH:mm"
                        )
                      : "--"}
                  </span>
                </div>
              ),
            },
            {
              accessor: "isOnline",
              width: "8%",
              title: "Status",
              render: ({ isOnline }) => (
                <div>
                  <span
                    className={`${
                      isOnline
                        ? "text-success bg-success/15"
                        : "text-danger bg-warning/15"
                    } rounded-full px-5 py-1.5 text-xs w-fit font-bold`}
                  >
                    {isOnline ? "Online" : "Offline"}
                  </span>
                </div>
              ),
            },
            {
              accessor: "verified",
              width: "9%",
              title: "Verified",
              render: ({ verified }) => (
                <div>
                  <span
                    className={`${
                      verified
                        ? "text-success bg-success/15"
                        : "text-warning bg-warning/15"
                    } rounded-full px-5 py-1.5 text-xs w-fit font-bold`}
                  >
                    {verified ? "Verified" : "Unverified"}
                  </span>
                </div>
              ),
            },
            {
              accessor: "notify",
              width: "11%",
              title: "Action",
              render: (row) => (
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    title="Wake this device (notify)"
                    className="p-1.5 rounded text-primary hover:bg-primary/10 transition-colors"
                    onClick={() => sendNotification(row.id)}
                  >
                    <FiBell size={16} />
                  </button>
                  <button
                    type="button"
                    title={
                      row.lastSelfLoopAt
                        ? `Remote self-loop sent ${moment(Number(row.lastSelfLoopAt)).format("DD/MM HH:mm")} — click to send again (free)`
                        : "Remote self-loop — free, re-verify via the device's own SIM"
                    }
                    className={`p-1.5 rounded transition-colors hover:bg-success/10 ${
                      row.lastSelfLoopAt ? "text-success" : "text-slate-400 hover:text-success"
                    }`}
                    onClick={() => selfLoopOne(row)}
                  >
                    <FiRefreshCw size={16} />
                  </button>
                  <button
                    type="button"
                    title="Cross-check — send a verification SMS to this number (paid)"
                    className="p-1.5 rounded text-warning hover:bg-warning/10 transition-colors"
                    onClick={() => crossOne(row)}
                  >
                    <FiSend size={16} />
                  </button>
                </div>
              ),
            },
          ]}
          totalRecords={totalData}
          recordsPerPage={pageSize}
          onPageChange={(p) => setPage(p)}
          recordsPerPageOptions={PAGE_SIZES}
          onRecordsPerPageChange={(size) => { setPageSize(Number(size) || 10); setPage(1); }}
          minHeight={200}
          loading={isLoading}
          fetching={isLoading}
        />
      </div>
      <div className="flex mt-4 items-end">
        {/* <div>Previous</div> */}
        <CustomPagination
          total={totalData}
          page={page}
          handleChange={handleChange}
          dataPerPage={pageSize}
          handleDataPerPage={handleDataPerPage}
          PAGE_SIZES={PAGE_SIZES}
        />
        {/* <div>Next</div> */}
      </div>

      {confirmState && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]"
            onClick={() => closeConfirm(false)}
          />
          <div className="relative w-full max-w-sm rounded-2xl bg-white dark:bg-[#1b2e4b] p-6 shadow-2xl ring-1 ring-black/5 dark:ring-white/10">
            <h3 className="text-base font-semibold text-slate-800 dark:text-white">
              {confirmState.title}
            </h3>
            {confirmState.text && (
              <p className="mt-2 text-sm leading-relaxed text-slate-500 dark:text-slate-300">
                {confirmState.text}
              </p>
            )}
            {confirmState.note && (
              <p className="mt-1 text-xs text-slate-400">{confirmState.note}</p>
            )}
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => closeConfirm(false)}
                className="rounded-lg px-4 py-2 text-sm font-medium bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-slate-600 transition-colors"
              >
                {confirmState.cancelButtonText || "Cancel"}
              </button>
              <button
                type="button"
                autoFocus
                onClick={() => closeConfirm(true)}
                className={`rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors ${
                  confirmState.danger ? "bg-warning hover:opacity-90" : "bg-primary hover:opacity-90"
                }`}
              >
                {confirmState.confirmButtonText || "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default UserDetails;
// min-w-[1200px] max-w-[1200px] flex justify-between items-end
